import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    catchError,
    concatMap,
    exhaustMap, forkJoin,
    map,
    mergeMap,
    of,
    switchMap,
    withLatestFrom,
} from 'rxjs';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import {
    ManualDistributedEntry,
    MatchGameResponse,
    MatchResponse,
    TournamentMatchResponse,
} from '../../models/api/responses/tournamentBrackets.response';
import { TournamentBracketsService } from '../../services/tournament-brackets.service';
import { TournamentBracketsActions } from '../actions/actions-type.action';
import { Store } from '@ngrx/store';
import { AppState } from '../../../shared/store/appState';
import { selectCurrentTournamentId } from '../selectors/tournamentGeneral.selector';

@Injectable()
export class TournamentBracketsEffect {
    private actions$ = inject(Actions);
    private tournamentBracketsService = inject(TournamentBracketsService);
    private notificationsService = inject(NotificationsService);
    private store = inject<Store<AppState>>(Store);

    getTournamentBrackets = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchTournamentBrackets),
            switchMap((action) =>
                this.tournamentBracketsService
                    .getTournamentBrackets(action.payload)
                    .pipe(
                        map((response: TournamentMatchResponse[]) =>
                            TournamentBracketsActions.fetchTournamentBracketsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchTournamentBracketsFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    finalizeSupervisorReportV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.supervisorFinalizeReportTournamentMatchResultV2),
            concatMap(({ payload: { tournamentId, groupId, matchId, additionalSideEffects, nextStep } }) =>
                this.tournamentBracketsService
                    .finalizeSupervisorMatchReportV2(tournamentId, groupId, matchId)
                    .pipe(
                        concatMap((response: any) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_MATCH_RESULTS_SAVED,
                                type: SnackBarType.Success,
                            });
                            return [
                                TournamentBracketsActions.supervisorFinalizeReportTournamentMatchResultSuccess({
                                    payload: {
                                        ...response,
                                        tournamentId,
                                        groupId,
                                        matchId,
                                        nextStep,
                                    } as any,
                                }),
                                TournamentBracketsActions.fetchTournamentGroupV2({
                                    payload: { tournamentId, groupId },
                                }),
                                TournamentBracketsActions.fetchSubmittedGameRoundsResultsV2({
                                    payload: { tournamentId, groupId, matchId },
                                }),
                                TournamentBracketsActions.fetchTournamentGroupMatchGameV2({
                                    payload: { tournamentId, groupId, matchId },
                                }),
                                ...(additionalSideEffects || []),
                            ];
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.supervisorFinalizeReportTournamentMatchResultFailure({
                                    payload: {
                                        ...response,
                                        tournamentId,
                                        groupId,
                                        matchId,
                                    } as any,
                                })
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentMatchById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchTournamentMatch),
            switchMap((action) =>
                this.tournamentBracketsService
                    .fetchTournamentMatch(action.payload)
                    .pipe(
                        map((response: MatchResponse) =>
                            TournamentBracketsActions.fetchTournamentMatchSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchTournamentMatchFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentMatchByIdV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchTournamentMatchV2),
            switchMap((action) =>
                this.tournamentBracketsService
                    .fetchTournamentMatchV2(action.payload)
                    .pipe(
                        map((response: MatchResponse) =>
                            TournamentBracketsActions.fetchTournamentMatchV2Success(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchTournamentMatchV2Failure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentGroupMatchByIdV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchTournamentGroupV2),
            switchMap((action) =>
                this.tournamentBracketsService
                    .fetchTournamentGroupMatchV2(action.payload)
                    .pipe(
                        map((response: MatchResponse) =>
                            TournamentBracketsActions.fetchTournamentMatchV2Success(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchTournamentMatchV2Failure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentMatchGameById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchTournamentMatchGame),
            switchMap((action) =>
                this.tournamentBracketsService
                    .fetchTournamentMatchGame(action.payload)
                    .pipe(
                        map((response: MatchGameResponse) =>
                            TournamentBracketsActions.fetchTournamentMatchGameSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchTournamentMatchGameFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchSubmittedScreenshots$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentBracketsActions.fetchSubmittedTournamentMatchResultScreenshots
            ),
            switchMap((action) =>
                this.tournamentBracketsService
                    .fetchSubmittedScreenshots(action.payload)
                    .pipe(
                        map((response) =>
                            TournamentBracketsActions.fetchSubmittedTournamentMatchResultScreenshotsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchSubmittedTournamentMatchResultScreenshotsFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchSubmittedGroupScreenshotsV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentBracketsActions.fetchSubmittedTournamentGroupMatchResultScreenshotsV2
            ),
            switchMap((action: any) => {
                const { tournamentId, groupId, matchId } = action.payload;
                const matchIds = Array.isArray(matchId) ? matchId : [matchId];
                const requests = matchIds.map((id) =>
                    this.tournamentBracketsService.fetchSubmittedGroupScreenshotsV2({
                        tournamentId,
                        groupId,
                        matchId: id,
                    })
                );

                return forkJoin(requests).pipe(
                    map((responses) =>
                        TournamentBracketsActions.fetchSubmittedTournamentMatchResultScreenshotsSuccess({
                            payload: responses.flat(),
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            TournamentBracketsActions.fetchSubmittedTournamentMatchResultScreenshotsFailure({
                                payload: response,
                            })
                        )
                    )
                );
            })
        )
    );

    fetchGameRoundsResults$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchSubmittedGameRoundsResults),
            switchMap((action) =>
                this.tournamentBracketsService
                    .fetchGameRoundsResult(action.payload)
                    .pipe(
                        map((response) =>
                            TournamentBracketsActions.fetchSubmittedGameRoundsResultsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchSubmittedGameRoundsResultsFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchGameRoundsResultsAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchGamesScoresInGroupRequest),
            mergeMap((action) => {
                const {games} = action.payload;
                const requests = games.map((game: any) =>
                    this.tournamentBracketsService.fetchGameRoundsResult({
                        tournamentId: action.payload.tournamentId,
                        matchId: action.payload.matchId,
                        matchGameId: game.id,
                    })
                );
                return forkJoin(requests).pipe(
                    map((responses: any) => TournamentBracketsActions.fetchGamesScoresInGroupSuccess({payload:responses})),
                    catchError((response: BadResponseWithError) =>
                        of(
                            TournamentBracketsActions.fetchGamesScoresInGroupFailure(
                                {payload: response}
                            )
                        )
                    )
                )
            })
        ));

    fetchGameRoundsResultsAdminV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchGamesScoresInGroupRequestV2),
            mergeMap((action) => {
                const { games, tournamentId, groupId } = action.payload as any;
                const requests = games.map((game: any) =>
                    this.tournamentBracketsService.fetchGroupGameRoundsResultV2({
                        tournamentId,
                        groupId,
                        matchId: game.id,
                    })
                );
                return forkJoin(requests).pipe(
                    map((responses: any) =>
                        TournamentBracketsActions.fetchGamesScoresInGroupSuccess({ payload: responses.flat() })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            TournamentBracketsActions.fetchGamesScoresInGroupFailure({ payload: response })
                        )
                    )
                );
            })
        )
    );

    fetchSubmittedGameRoundsResultsV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchSubmittedGameRoundsResultsV2),
            switchMap((action: any) => {
                const { tournamentId, groupId, matchId } = action.payload;
                const matchIds = Array.isArray(matchId) ? matchId : [matchId];
                const requests = matchIds.map((id) =>
                    this.tournamentBracketsService.fetchGroupGameRoundsResultV2({
                        tournamentId,
                        groupId,
                        matchId: id,
                    })
                );

                return forkJoin(requests).pipe(
                    map((responses) =>
                        TournamentBracketsActions.fetchSubmittedGameRoundsResultsSuccess({
                            payload: responses.flat(),
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            TournamentBracketsActions.fetchSubmittedGameRoundsResultsFailure({
                                payload: response,
                            })
                        )
                    )
                );
            })
        )
    );

    reportTournamentMatchResults$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.reportTournamentMatchResult),
            exhaustMap((action) =>
                this.tournamentBracketsService
                    .reportTournamentMatchResults(action.payload)
                    .pipe(
                        concatMap((response) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_MATCH_RESULTS_SAVED,
                                type: SnackBarType.Success,
                            });

                            if (action.payload.onSuccess) {
                                action.payload.onSuccess();
                            }

                            return [
                                TournamentBracketsActions.reportTournamentMatchResultSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                TournamentBracketsActions.fetchSubmittedGameRoundsResults(
                                    {
                                        payload: {
                                            tournamentId:
                                                action.payload.tournamentId,
                                            matchId: action.payload.matchId,
                                            matchGameId:
                                                action.payload.matchGameId,
                                        },
                                    }
                                ),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TournamentBracketsActions.reportTournamentMatchResultFailure(
                                    { payload: response }
                                )
                            );
                        })
                    )
            )
        )
    );

    reportTournamentMatchResultsV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.reportTournamentMatchResultV2),
            concatMap((action) =>
                this.tournamentBracketsService
                    .reportTournamentMatchResultsV2(action.payload)
                    .pipe(
                        concatMap((response) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_MATCH_RESULTS_SAVED,
                                type: SnackBarType.Success,
                            });

                            if (action.payload.onSuccess) {
                                action.payload.onSuccess();
                            }

                            return [
                                TournamentBracketsActions.reportTournamentMatchResultSuccess({
                                    payload: {
                                        ...response,
                                        nextStep: action.payload.nextStep,
                                    } as any,
                                }),
                                TournamentBracketsActions.fetchSubmittedGameRoundsResultsV2({
                                    payload: {
                                        tournamentId: action.payload.tournamentId,
                                        groupId: action.payload.groupId,
                                        matchId: action.payload.matchId,
                                    },
                                }),
                                TournamentBracketsActions.fetchSubmittedTournamentGroupMatchResultScreenshotsV2({
                                    payload: {
                                        tournamentId: action.payload.tournamentId,
                                        groupId: action.payload.groupId,
                                        matchId: action.payload.matchId,
                                    },
                                }),
                                TournamentBracketsActions.fetchTournamentGroupV2({
                                    payload: {
                                        tournamentId: action.payload.tournamentId,
                                        groupId: action.payload.groupId,
                                    },
                                }),
                                TournamentBracketsActions.fetchTournamentGroupMatchGameV2({
                                    payload: {
                                        tournamentId: action.payload.tournamentId,
                                        groupId: action.payload.groupId,
                                        matchId: action.payload.matchId,
                                    },
                                }),
                                ...(action.payload.additionalSideEffects || []),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentBracketsActions.reportTournamentMatchResultFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
    finalizeSupervisorReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.supervisorFinalizeReportTournamentMatchResult),
            exhaustMap((action) =>
                this.tournamentBracketsService
                    .finalizeSupervisorMatchReport(action.payload.tournamentId, action.payload.matchId)
                    .pipe(
                        concatMap((response) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_MATCH_RESULTS_SAVED,
                                type: SnackBarType.Success,
                            });
                            return [
                                TournamentBracketsActions.supervisorFinalizeReportTournamentMatchResultSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                TournamentBracketsActions.fetchSubmittedGameRoundsResults(
                                    {
                                        payload: {
                                            tournamentId:
                                            action.payload.tournamentId,
                                            matchId: action.payload.matchId,
                                            matchGameId:
                                            action.payload.matchGameId,
                                        },
                                    }
                                ),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TournamentBracketsActions.supervisorFinalizeReportTournamentMatchResultFailure(
                                    { payload: response }
                                )
                            );
                        })
                    )
            )
        )
    );

    publishTournamentMatchResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentBracketsActions.supervisorReportTournamentMatchResult
            ),
            concatMap((action) =>
                this.tournamentBracketsService
                    .supervisorReportTournamentMatchResult(action.payload)
                    .pipe(
                        concatMap((response) => {
                            const sideEffects: any[] = [];
                            // const sideEffects: TypedAction<string>[] = [];

                            if (action.payload.publish) {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.TOURNAMENT_MATCH_RESULTS_PUBLISHED,
                                    type: SnackBarType.Success,
                                });

                                if (action.payload.onPublish) {
                                    action.payload.onPublish();
                                }
                            } else if (
                                action.payload
                                    .shouldBePublishedAfterSuccessUpload
                            ) {
                                sideEffects.push(
                                    TournamentBracketsActions.supervisorReportTournamentMatchResult(
                                        {
                                            payload: {
                                                publish: true,
                                                tournamentId:
                                                    action.payload.tournamentId,
                                                matchId: action.payload.matchId,
                                                matchGameId:
                                                    action.payload.matchGameId,
                                                requestData: {},
                                                onPublish:
                                                    action.payload.onPublish,
                                            },
                                        }
                                    )
                                );
                            } else {
                                //Update the results table
                                sideEffects.push(
                                    TournamentBracketsActions.fetchSubmittedGameRoundsResults(
                                        {
                                            payload: {
                                                tournamentId:
                                                    action.payload.tournamentId,
                                                matchId: action.payload.matchId,
                                                matchGameId:
                                                    action.payload.matchGameId,
                                            },
                                        }
                                    )
                                );

                                if (action.payload.onReport) {
                                    action.payload.onReport();
                                }
                            }

                            return [
                                TournamentBracketsActions.supervisorReportTournamentMatchResultSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                ...sideEffects,
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentBracketsActions.supervisorReportTournamentMatchResultFailure(
                                    { payload: response }
                                )
                            );
                        })
                    )
            )
        )
    );

    publishTournamentMatchResultV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentBracketsActions.supervisorReportTournamentMatchResultV2
            ),
            concatMap((action) =>
                {
                    return this.tournamentBracketsService
                        .supervisorReportTournamentMatchResultV2(action.payload)
                        .pipe(
                            concatMap((response) => {
                                const sideEffects: any[] = [];

                                if (action.payload.publish) {
                                    this.notificationsService.openSnackBar({
                                        title: NOTIFICATIONS.TOURNAMENT_MATCH_RESULTS_PUBLISHED,
                                        type: SnackBarType.Success,
                                    });
                                    if (action.payload.onPublish) {
                                        action.payload.onPublish();
                                    }
                                    sideEffects.push(
                                        TournamentBracketsActions.fetchSubmittedTournamentGroupMatchResultScreenshotsV2({
                                            payload: {
                                                tournamentId: action.payload.tournamentId,
                                                groupId: action.payload.groupId,
                                                matchId: action.payload.matchId,
                                            },
                                        })
                                    );
                                    sideEffects.push(
                                        TournamentBracketsActions.fetchTournamentGroupV2({
                                            payload: {
                                                tournamentId: action.payload.tournamentId,
                                                groupId: action.payload.groupId,
                                            },
                                        })
                                    );
                                } else if (
                                    action.payload.shouldBePublishedAfterSuccessUpload
                                ) {
                                    sideEffects.push(
                                        TournamentBracketsActions.supervisorReportTournamentMatchResultV2({
                                            payload: {
                                                publish: true,
                                                tournamentId: action.payload.tournamentId,
                                                groupId: action.payload.groupId,
                                                matchId: action.payload.matchId,
                                                participantId: action.payload.participantId,
                                                requestData: {},
                                                onPublish: action.payload.onPublish,
                                            },
                                        })
                                    );
                                } else {
                                    sideEffects.push(
                                        TournamentBracketsActions.fetchSubmittedTournamentGroupMatchResultScreenshotsV2({
                                            payload: {
                                                tournamentId: action.payload.tournamentId,
                                                groupId: action.payload.groupId,
                                                matchId: action.payload.matchId,
                                            },
                                        })
                                    );

                                    if (action.payload.onReport) {
                                        action.payload.onReport();
                                    }
                                    sideEffects.push(
                                        TournamentBracketsActions.fetchTournamentGroupV2({
                                            payload: {
                                                tournamentId: action.payload.tournamentId,
                                                groupId: action.payload.groupId,
                                            },
                                        })
                                    );
                                    sideEffects.push(
                                        TournamentBracketsActions.fetchTournamentGroupMatchGameV2({
                                            payload: {
                                                tournamentId: action.payload.tournamentId,
                                                groupId: action.payload.groupId,
                                                matchId: action.payload.matchId,
                                            },
                                        })
                                    );
                                }

                                return [
                                    TournamentBracketsActions.supervisorReportTournamentMatchResultSuccess({
                                        payload: {
                                            ...response,
                                            nextStep: action.payload.nextStep,
                                        } as any,
                                    }),
                                    ...sideEffects,
                                    ...(action.payload.additionalSideEffects || []),
                                ];
                            }),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error?.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                                return of(
                                    TournamentBracketsActions.supervisorReportTournamentMatchResultFailure(
                                        { payload: response }
                                    )
                                );
                            })
                        )
                }
            )
        )
    );

    saveTournamentMatch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.saveTournamentMatch),
            exhaustMap(({ payload: { tournamentId, matchId, requestData } }) =>
                this.tournamentBracketsService
                    .saveTournamentMatch(tournamentId, matchId, requestData)
                    .pipe(
                        concatMap((response: MatchResponse) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_MATCH_SAVED,
                                type: SnackBarType.Success,
                            });
                            return [
                                TournamentBracketsActions.saveTournamentMatchSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                TournamentBracketsActions.fetchTournamentMatchV2({
                                    payload: { tournamentId, matchId },
                                }),
                            ];
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.saveTournamentMatchFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveTournamentMatchGame$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.saveTournamentMatchGame),
            exhaustMap(
                ({
                    payload: {
                        tournamentId,
                        matchId,
                        matchGameId,
                        requestData,
                    },
                }) =>
                    this.tournamentBracketsService
                        .saveTournamentMatchGame(
                            tournamentId,
                            matchId,
                            matchGameId,
                            requestData
                        )
                        .pipe(
                            concatMap((response: MatchGameResponse) => {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.TOURNAMENT_MATCH_GAME_SAVED,
                                    type: SnackBarType.Success,
                                });
                                return [
                                    TournamentBracketsActions.saveTournamentMatchGameSuccess(
                                        {
                                            payload: response,
                                        }
                                    ),
                                    TournamentBracketsActions.fetchTournamentMatchV2({
                                        payload: { tournamentId, matchId },
                                    }),
                                ];
                            }),
                            catchError((response: BadResponseWithError) =>
                                of(
                                    TournamentBracketsActions.saveTournamentMatchGameFailure(
                                        { payload: response }
                                    )
                                )
                            )
                        )
            )
        )
    );

    fetchTournamentGroupMatchGameV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchTournamentGroupMatchGameV2),
            exhaustMap(({ payload: { tournamentId, groupId, matchId } }) =>
                this.tournamentBracketsService
                    .fetchTournamentGroupMatchGameV2({ tournamentId, groupId, matchId })
                    .pipe(
                        map((response: any) =>
                            TournamentBracketsActions.fetchTournamentGroupMatchGameV2Success({ payload: response })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(TournamentBracketsActions.fetchTournamentGroupMatchGameV2Failure({ payload: response }))
                        )
                    )
            )
        )
    );

    saveTournamentGroupMatchV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.saveTournamentGroupMatchV2),
            exhaustMap(({ payload: { tournamentId, groupId, matchId, requestData } }) =>
                this.tournamentBracketsService
                    .saveTournamentGroupMatchV2(tournamentId, groupId, matchId, requestData)
                    .pipe(
                        concatMap((response: MatchResponse) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_MATCH_SAVED,
                                type: SnackBarType.Success,
                            });
                            return [
                                TournamentBracketsActions.saveTournamentGroupMatchV2Success({
                                    payload: response,
                                }),
                                TournamentBracketsActions.fetchTournamentGroupV2({
                                    payload: { tournamentId, groupId },
                                }),
                                TournamentBracketsActions.fetchTournamentGroupMatchGameV2({
                                    payload: { tournamentId, groupId, matchId },
                                }),
                            ];
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.saveTournamentGroupMatchV2Failure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    removeScreenshotBySupervisor = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.removeScreenshotBySupervisor),
            exhaustMap((action) =>
                this.tournamentBracketsService
                    .removeScreenshotBySupervisor(action.payload)
                    .pipe(
                        concatMap(() => {
                            return [
                                TournamentBracketsActions.removeScreenshotBySupervisorSuccess({
                                    payload: action.payload.screenshotId,
                                }),
                                TournamentBracketsActions.fetchSubmittedTournamentGroupMatchResultScreenshotsV2({
                                    payload: {
                                        tournamentId: action.payload.tournamentId,
                                        groupId: action.payload.matchId,
                                        matchId: action.payload.matchGameId,
                                    },
                                }),
                            ];
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.removeScreenshotBySupervisorFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    removeScreenshotBySupervisorV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.removeScreenshotBySupervisorV2),
            exhaustMap(({ payload }) =>
                this.tournamentBracketsService
                    .removeGroupScreenshotBySupervisorV2(payload)
                    .pipe(
                        concatMap(() => {
                            return [
                                TournamentBracketsActions.removeScreenshotBySupervisorV2Success({
                                    payload: payload.screenshotId,
                                }),
                                TournamentBracketsActions.fetchSubmittedTournamentGroupMatchResultScreenshotsV2({
                                    payload: {
                                        tournamentId: payload.tournamentId,
                                        groupId: payload.groupId,
                                        matchId: payload.matchId,
                                    },
                                }),
                            ];
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.removeScreenshotBySupervisorV2Failure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    confirmRoundFormat$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.confirmRoundFormat),
            exhaustMap(
                ({
                    payload: {
                        type,
                        level,
                        format,
                        tournamentId,
                        rules,
                        gameMaps,
                    },
                }) =>
                    this.tournamentBracketsService
                        .changeRoundFormat(tournamentId, {
                            type,
                            level,
                            format,
                            rules,
                            gameMaps,
                        })
                        .pipe(
                            map((response: TournamentMatchResponse[]) => {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.ROUND_FORMAT_CHANGED,
                                    type: SnackBarType.Success,
                                });

                                return TournamentBracketsActions.confirmRoundFormatSuccess(
                                    {
                                        payload: response,
                                    }
                                );
                            }),
                            catchError((response: BadResponseWithError) =>
                                of(
                                    TournamentBracketsActions.confirmRoundFormatFailure(
                                        { payload: response }
                                    )
                                )
                            )
                        )
            )
        )
    );

    fetchRoundDataV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchRoundDataV2),
            withLatestFrom(this.store.select(selectCurrentTournamentId)),
            switchMap(([{ payload: { roundId } }, tournamentId]) =>
                this.tournamentBracketsService
                    .getRoundDataV2(tournamentId, roundId)
                    .pipe(
                        map((data: any) =>
                            TournamentBracketsActions.fetchRoundDataV2Success({ payload: data })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(TournamentBracketsActions.fetchRoundDataV2Failure({ payload: response }))
                        )
                    )
            )
        )
    );
    confirmRoundFormatV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.confirmRoundFormatV2),
            switchMap(({ payload }) =>
                this.tournamentBracketsService
                    .changeRoundFormatV2(payload.tournamentId, {
                        roundId: payload.roundId,
                        rules: payload.rules,
                        numberOfMatches: payload.numberOfMatches,
                        maps: payload.maps,
                    })
                    .pipe(
                        map((response: any) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.ROUND_FORMAT_CHANGED,
                                type: SnackBarType.Success,
                            });
                            return TournamentBracketsActions.confirmRoundFormatV2Success({ payload: response });
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(TournamentBracketsActions.confirmRoundFormatV2Failure({ payload: response }))
                        )
                    )
            )
        )
    );

    fetchAllowedMatchesForWinCondition$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.fetchAllowedMatchesForWinCondition),
            switchMap(({ payload: { condition } }) =>
                this.tournamentBracketsService
                    .getAllowedMatchesForWinCondition(condition)
                    .pipe(
                        map((resp) =>
                            TournamentBracketsActions.fetchAllowedMatchesForWinConditionSuccess({
                                payload: { condition: resp.condition || condition, allowedValues: resp.allowedValues || [] },
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.fetchAllowedMatchesForWinConditionFailure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );


    manualDescriptionFetchEntries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.manualDistributionFetchEntries),
            switchMap(({ payload: { tournamentId } }) =>
                this.tournamentBracketsService
                    .manualDistributionFetchEntries(tournamentId)
                    .pipe(
                        map((response: ManualDistributedEntry[]) =>
                            TournamentBracketsActions.manualDistributionFetchEntriesSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.manualDistributionFetchEntriesFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    manualDescriptionGenerateEntries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentBracketsActions.manualDistributionGenerateEntries),
            switchMap(({ payload: { tournamentId } }) =>
                this.tournamentBracketsService
                    .manualDistributionGenerateEntries(tournamentId)
                    .pipe(
                        map((response: ManualDistributedEntry[]) =>
                            TournamentBracketsActions.manualDistributionGenerateEntriesSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.manualDistributionGenerateEntriesFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    manualDescriptionDistributeParticipants$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentBracketsActions.manualDistributionDistributeParticipants
            ),
            switchMap(({ payload }) =>
                this.tournamentBracketsService
                    .manualDistributionDistributeParticipants(payload)
                    .pipe(
                        mergeMap((response: ManualDistributedEntry) => [
                            TournamentBracketsActions.manualDistributionDistributeParticipantsSuccess(
                                {
                                    payload: response,
                                }
                            ),
                            TournamentBracketsActions.manualDistributionFetchEntries(
                                {
                                    payload: {
                                        tournamentId: payload.tournamentId,
                                    },
                                }
                            ),
                        ]),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentBracketsActions.manualDistributionDistributeParticipantsFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );
}
